import * as React from "react";
import * as mutations from "../../graphql/mutations";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StartIcon from "@mui/icons-material/Start";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import DeletionModal from "../../components/modals/DeletionModal";
import LoadingModal from "../../components/modals/LoadingModal";
import NotificationSnackbar from "../../components/NotificationSnackbar";
import CreateUpdatePublicAssessmentModal from "../../components/modals/CreateUpdatePublicAssessmentModal";
import { apiClient } from "../../helpers/apiClient";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchAllPublicAssessments } from "../../helpers/apiRequests";
import {
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

export default function ManagePublicAssessments() {
  const { t } = useTranslation();

  const [openCreateUpdate, setOpenCreateUpdate] = React.useState(false);
  const [editAssessmentData, setEditAssessmentData] = React.useState({});

  const [openNotification, setOpenNotification] = React.useState(false);
  const [notificationMessage, setNotificationMessage] = React.useState("");

  const [openLoading, setOpenLoading] = React.useState(false);

  const [openDelete, setOpenDelete] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState("");

  async function deletePublicAssessment(toDeleteId) {
    try {
      await apiClient.graphql({
        query: mutations.deleteAssessment,
        variables: {
          input: {
            id: toDeleteId,
          },
        },
      });
      fetchPublicAssessments();
      setNotificationMessage(t("itemDeleted!"));
    } catch (e) {
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
  }

  const [publicAssessments, setPublicAssessments] = React.useState([]);

  async function fetchPublicAssessments() {
    setOpenLoading(true);
    try {
      const fetchedPublicAssessments = await fetchAllPublicAssessments();
      setPublicAssessments(fetchedPublicAssessments);
    } catch {
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  async function updatePublicAssessmentState(toDeleteId, newState) {
    try {
      await apiClient.graphql({
        query: mutations.updateAssessment,
        variables: {
          input: {
            id: toDeleteId,
            active: newState,
          },
        },
      });
      fetchPublicAssessments();
      setNotificationMessage(t("assessmentStatusUpdated"));
    } catch (e) {
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
  }

  React.useEffect(() => {
    fetchPublicAssessments();
  }, []);

  const navigate = useNavigate();

  return (
    <Container maxWidth="xl">
      <Box sx={{ width: 1, display: "flex", flexDirection: "column", mt: 5 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: 18 }}>
            {t("createdPublicAssessments")}
          </Typography>
          <Button
            onClick={() => {
              setEditAssessmentData({});
              setOpenCreateUpdate(true);
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ borderRadius: 10, py: 0, textTransform: "none" }}
          >
            {t("createNewAssesment")}
          </Button>
        </Box>
        <Divider sx={{ mt: 1 }} />
        {publicAssessments.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              fontStyle: "italic",
              mt: 4,
            }}
          >
            <Typography sx={{ fontFamily: "Inter", fontSize: 16 }}>
              {t("noPublicAssessmentsYet")}
            </Typography>
          </Box>
        )}
        {publicAssessments.map((assessmentData, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              borderBottom: 1,
              borderColor: "#bbb",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  ml: 2,
                  fontFamily: "Inter",
                  minWidth: 300,
                  maxWidth: 300,
                }}
              >
                Title: {assessmentData.name}
              </Typography>
              <Typography
                align="right"
                sx={{
                  ml: 2,
                  fontFamily: "Inter",
                }}
              >
                Id: {assessmentData.id}
              </Typography>{" "}
              <Tooltip title={t("openAssessment")}>
                <IconButton
                  sx={{ width: 25, height: 25, ml: 1, mr: 5 }}
                  disabled={!assessmentData?.active}
                  onClick={() =>
                    window.open(
                      "/publicAssessments" + "/" + assessmentData.id,
                      "_blank"
                    )
                  }
                >
                  <OpenInNewIcon style={{ width: 17, height: 17 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Tooltip title={t("viewParticipantData")}>
                <IconButton
                  sx={{ mr: 3 }}
                  onClick={() =>
                    window.open(
                      "/publicAssessmentData" + "/" + assessmentData.id,
                      "_blank"
                    )
                  }
                >
                  <ViewListOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Typography
                sx={{
                  fontSize: 12,
                  pr: 1,
                  fontStyle: "italic",
                  mr: -1,
                }}
              >
                Active:
              </Typography>
              <Switch
                sx={{ mr: 2 }}
                checked={assessmentData?.active || false}
                onChange={(e) => {
                  e.stopPropagation();
                  updatePublicAssessmentState(
                    assessmentData.id,
                    e.target.checked
                  );
                }}
              />{" "}
              <Tooltip title={t("editTitle")}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditAssessmentData(assessmentData);
                    setOpenCreateUpdate(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>{" "}
              <Tooltip title={t("deleteAssessment")}>
                <IconButton
                  sx={{ ml: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDeleteId(assessmentData.id);
                    setOpenDelete(true);
                  }}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
              <Button
                sx={{
                  borderRadius: 0,
                  ml: 2.5,
                  borderLeft: 1,
                  minWidth: 200,
                  maxWidth: 200,
                }}
                endIcon={<StartIcon />}
                onClick={() =>
                  navigate("/managePublicAssessments/" + assessmentData.id)
                }
              >
                {t("manageAssessment")}
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
      <CreateUpdatePublicAssessmentModal
        open={openCreateUpdate}
        setOpen={setOpenCreateUpdate}
        editAssessmentData={editAssessmentData}
        setEditAssessmentData={setEditAssessmentData}
        setNotificationMessage={setNotificationMessage}
        setOpenNotification={setOpenNotification}
        fetchData={fetchPublicAssessments}
      />
      <LoadingModal open={openLoading} />
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
      <DeletionModal
        open={openDelete}
        setOpen={setOpenDelete}
        deleteFunction={deletePublicAssessment}
        safeDelete={true}
        itemClass={"Assessment"}
        id={toDeleteId}
      />
    </Container>
  );
}
